import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from './UnifiedAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { 
  Clock, FileText, LogOut, 
  User, Building2, ChevronRight,
  CalendarDays, UserCheck, AlertTriangle, Shield, Users, Scale, CheckCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import UnifiedNotificacoes from './UnifiedNotificacoes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UnifiedHome = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, hasJornyxAccess, jornyxUser, jornyxToken } = useUnifiedAuth();
  
  // Estados para contagem de pendentes
  const [pendentesAprovacao, setPendentesAprovacao] = React.useState(0);
  const [pendentesDisciplinares, setPendentesDisciplinares] = React.useState(0);
  const [pendentesInconsistencias, setPendentesInconsistencias] = React.useState(0);
  
  // Estado para notificação disciplinar bloqueante
  const [notificacaoBloqueante, setNotificacaoBloqueante] = React.useState(null);
  const [confirmandoRecebimento, setConfirmandoRecebimento] = React.useState(false);

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/unified/login');
    }
  }, [isAuthenticated, navigate]);

  // Verificar notificação disciplinar pendente (bloqueante)
  React.useEffect(() => {
    if (jornyxToken && hasJornyxAccess) {
      fetch(`${API_URL}/api/app/notificacoes-disciplinares-pendentes`, {
        headers: { 'Authorization': `Bearer ${jornyxToken}` }
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.tem_pendente && data.notificacao) {
            setNotificacaoBloqueante(data.notificacao);
          }
        })
        .catch(() => {});
    }
  }, [jornyxToken, hasJornyxAccess]);

  // Buscar contadores de pendentes
  React.useEffect(() => {
    if (jornyxToken && hasJornyxAccess) {
      // Buscar justificativas pendentes da equipe (para gestores)
      if (jornyxUser?.is_gestor) {
        fetch(`${API_URL}/api/app/justificativas-equipe`, {
          headers: { 'Authorization': `Bearer ${jornyxToken}` }
        })
          .then(res => res.ok ? res.json() : null)
          .then(data => {
            if (data) setPendentesAprovacao(data.total_pendentes || 0);
          })
          .catch(() => {});
      }
      
      // Buscar notificações disciplinares pendentes
      fetch(`${API_URL}/api/app/notificacoes-disciplinares`, {
        headers: { 'Authorization': `Bearer ${jornyxToken}` }
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) setPendentesDisciplinares(data.total_pendentes || 0);
        })
        .catch(() => {});
      
      // Buscar inconsistências pendentes
      fetch(`${API_URL}/api/app/inconsistencias`, {
        headers: { 'Authorization': `Bearer ${jornyxToken}` }
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) {
            // API retorna {pendentes: [...], total_inconsistencias: N} - usar total ou contar pendentes
            setPendentesInconsistencias(data.pendentes?.length || data.total_inconsistencias || 0);
          }
        })
        .catch(() => {});
    }
  }, [jornyxToken, hasJornyxAccess, jornyxUser?.is_gestor]);

  const confirmarRecebimentoNotificacao = async () => {
    if (!notificacaoBloqueante) return;
    
    setConfirmandoRecebimento(true);
    try {
      const res = await fetch(`${API_URL}/api/app/notificacoes-disciplinares/${notificacaoBloqueante.id}/receber`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jornyxToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        toast.success('Recebimento confirmado!');
        setNotificacaoBloqueante(null);
        // Redirecionar para a tela de disciplinares para ver detalhes e contestar se desejar
        navigate('/unified/disciplinares');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao confirmar');
      }
    } catch (err) {
      toast.error('Erro ao confirmar recebimento');
    } finally {
      setConfirmandoRecebimento(false);
    }
  };

  const formatarData = (data) => {
    if (!data) return '-';
    try {
      if (data.includes('T')) {
        const d = new Date(data);
        return d.toLocaleDateString('pt-BR');
      }
      const [ano, mes, dia] = data.split('-');
      return `${dia}/${mes}/${ano}`;
    } catch {
      return data;
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logout realizado');
    navigate('/unified/login');
  };

  const handlePontoClick = () => {
    if (!hasJornyxAccess) {
      toast.error('Você não possui vínculo com nenhuma empresa no Jornyx. Entre em contato com o RH.');
      return;
    }
    
    // Verificar se empregado tem biometria ativa e app_ponto_autorizado = false
    if (jornyxUser?.tem_biometria && !jornyxUser?.app_ponto_autorizado) {
      toast.error('Você deve registrar ponto pelo totem com reconhecimento facial.');
      return;
    }
    
    navigate('/unified/ponto');
  };

  const handleInconsistenciasClick = () => {
    if (!hasJornyxAccess) {
      toast.error('Você não possui vínculo com nenhuma empresa no Jornyx. Entre em contato com o RH.');
      return;
    }
    navigate('/unified/inconsistencias');
  };

  const handleDisciplinaresClick = () => {
    if (!hasJornyxAccess) {
      toast.error('Você não possui vínculo com nenhuma empresa no Jornyx. Entre em contato com o RH.');
      return;
    }
    navigate('/unified/disciplinares');
  };

  const handleMarcacoesClick = () => {
    if (!hasJornyxAccess) {
      toast.error('Você não possui vínculo com nenhuma empresa no Jornyx. Entre em contato com o RH.');
      return;
    }
    navigate('/unified/marcacoes');
  };

  const handleAprovacoesClick = () => {
    if (!hasJornyxAccess) {
      toast.error('Você não possui vínculo com nenhuma empresa no Jornyx. Entre em contato com o RH.');
      return;
    }
    if (!jornyxUser?.is_gestor) {
      toast.error('Área exclusiva para gestores com subordinados cadastrados.');
      return;
    }
    navigate('/unified/aprovacoes');
  };

  const handleQuadroPresencaClick = () => {
    if (!hasJornyxAccess) {
      toast.error('Você não possui vínculo com nenhuma empresa no Jornyx. Entre em contato com o RH.');
      return;
    }
    navigate('/unified/quadro-presenca');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-slate-900 to-slate-900">
      {/* Background decorativo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative min-h-screen flex flex-col">
        {/* Header */}
        <header className="px-4 py-4 border-b border-white/10">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="font-semibold text-white">Jornyx</span>
            </div>
            <div className="flex items-center gap-2">
              {jornyxToken && <UnifiedNotificacoes token={jornyxToken} />}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-slate-400 hover:text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </header>

        {/* Conteúdo */}
        <main className="flex-1 px-4 py-6">
          <div className="max-w-lg mx-auto space-y-6">
            {/* Card do usuário */}
            <Card className="border-0 bg-white/5 backdrop-blur">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-white">
                      {user.nome || user.email}
                    </h2>
                    <p className="text-slate-400 text-sm">{user.email}</p>
                  </div>
                </div>

                {/* Status de vínculo - Apenas Jornyx */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-slate-300">Jornyx</span>
                    {hasJornyxAccess ? (
                      <span className="text-xs text-emerald-400 ml-auto">Vinculado</span>
                    ) : (
                      <span className="text-xs text-slate-500 ml-auto">Sem vínculo</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Título da seção */}
            <div>
              <h3 className="text-white font-medium mb-1">Escolha um módulo</h3>
              <p className="text-slate-400 text-sm">Acesse as funcionalidades disponíveis</p>
            </div>

            {/* Módulos */}
            <div className="space-y-3">
              {/* Módulo Ponto - Jornyx */}
              <Card 
                className={`border-0 backdrop-blur cursor-pointer transition-all group ${
                  hasJornyxAccess 
                    ? 'bg-gradient-to-r from-emerald-500/20 to-emerald-600/10 hover:from-emerald-500/30 hover:to-emerald-600/20' 
                    : 'bg-white/5 opacity-60'
                }`}
                onClick={handlePontoClick}
              >
                <CardContent className="py-5">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl shadow-lg ${
                      hasJornyxAccess 
                        ? 'bg-emerald-500 shadow-emerald-500/30' 
                        : 'bg-slate-600'
                    }`}>
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-white">Controle de Ponto</h4>
                        <Badge variant="outline" className="text-emerald-300 border-emerald-400/50 text-xs">
                          Jornyx
                        </Badge>
                      </div>
                      <p className="text-slate-400 text-sm mt-0.5">
                        {hasJornyxAccess 
                          ? 'Registre seu ponto e consulte marcações'
                          : 'Sem vínculo - Contate o RH'
                        }
                      </p>
                      {jornyxUser && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                          <Building2 className="w-3 h-3" />
                          {jornyxUser.empresa_nome || 'Empresa vinculada'}
                        </div>
                      )}
                    </div>
                    <ChevronRight className={`w-5 h-5 transition-transform ${
                      hasJornyxAccess 
                        ? 'text-emerald-400 group-hover:translate-x-1' 
                        : 'text-slate-600'
                    }`} />
                  </div>
                </CardContent>
              </Card>

              {/* Módulo Inconsistências e Justificativas - Unificado */}
              <Card 
                className={`border-0 backdrop-blur cursor-pointer transition-all group ${
                  hasJornyxAccess 
                    ? 'bg-gradient-to-r from-amber-500/20 to-orange-600/10 hover:from-amber-500/30 hover:to-orange-600/20' 
                    : 'bg-white/5 opacity-60'
                }`}
                onClick={handleInconsistenciasClick}
                data-testid="card-inconsistencias"
              >
                <CardContent className="py-5">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl shadow-lg relative ${
                      hasJornyxAccess 
                        ? 'bg-amber-500 shadow-amber-500/30' 
                        : 'bg-slate-600'
                    }`}>
                      <AlertTriangle className="w-6 h-6 text-white" />
                      {pendentesInconsistencias > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                          {pendentesInconsistencias > 9 ? '9+' : pendentesInconsistencias}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-white">Inconsistências</h4>
                        {pendentesInconsistencias > 0 && (
                          <Badge className="bg-red-500 text-white text-xs">
                            {pendentesInconsistencias} pendente{pendentesInconsistencias > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      <p className="text-slate-400 text-sm mt-0.5">
                        {hasJornyxAccess 
                          ? 'Veja e justifique marcações divergentes'
                          : 'Sem vínculo - Contate o RH'
                        }
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 transition-transform ${
                      hasJornyxAccess 
                        ? 'text-amber-400 group-hover:translate-x-1' 
                        : 'text-slate-600'
                    }`} />
                  </div>
                </CardContent>
              </Card>

              {/* Módulo Notificações Disciplinares */}
              <Card 
                className={`border-0 backdrop-blur cursor-pointer transition-all group ${
                  hasJornyxAccess 
                    ? pendentesDisciplinares > 0 
                      ? 'bg-gradient-to-r from-red-500/30 to-rose-600/20 hover:from-red-500/40 hover:to-rose-600/30 ring-1 ring-red-500/50'
                      : 'bg-gradient-to-r from-red-500/20 to-rose-600/10 hover:from-red-500/30 hover:to-rose-600/20'
                    : 'bg-white/5 opacity-60'
                }`}
                onClick={handleDisciplinaresClick}
                data-testid="card-disciplinares"
              >
                <CardContent className="py-5">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl shadow-lg relative ${
                      hasJornyxAccess 
                        ? 'bg-red-500 shadow-red-500/30' 
                        : 'bg-slate-600'
                    }`}>
                      <Shield className="w-6 h-6 text-white" />
                      {pendentesDisciplinares > 0 && (
                        <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                          {pendentesDisciplinares}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-white">Notificações Disciplinares</h4>
                        {pendentesDisciplinares > 0 && (
                          <Badge className="bg-amber-500 text-white text-xs animate-pulse">
                            {pendentesDisciplinares} para assinar
                          </Badge>
                        )}
                      </div>
                      <p className="text-slate-400 text-sm mt-0.5">
                        {hasJornyxAccess 
                          ? 'Advertências, suspensões e prontuário'
                          : 'Sem vínculo - Contate o RH'
                        }
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 transition-transform ${
                      hasJornyxAccess 
                        ? 'text-red-400 group-hover:translate-x-1' 
                        : 'text-slate-600'
                    }`} />
                  </div>
                </CardContent>
              </Card>

              {/* Módulo Marcações/Comprovante - Jornyx */}
              <Card 
                className={`border-0 backdrop-blur cursor-pointer transition-all group ${
                  hasJornyxAccess 
                    ? 'bg-gradient-to-r from-blue-500/20 to-indigo-600/10 hover:from-blue-500/30 hover:to-indigo-600/20' 
                    : 'bg-white/5 opacity-60'
                }`}
                onClick={handleMarcacoesClick}
              >
                <CardContent className="py-5">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl shadow-lg ${
                      hasJornyxAccess 
                        ? 'bg-blue-500 shadow-blue-500/30' 
                        : 'bg-slate-600'
                    }`}>
                      <CalendarDays className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-white">Comprovante de Ponto</h4>
                        <Badge variant="outline" className="text-blue-300 border-blue-400/50 text-xs">
                          671/2021
                        </Badge>
                      </div>
                      <p className="text-slate-400 text-sm mt-0.5">
                        {hasJornyxAccess 
                          ? 'Veja suas marcações diárias'
                          : 'Sem vínculo - Contate o RH'
                        }
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 transition-transform ${
                      hasJornyxAccess 
                        ? 'text-blue-400 group-hover:translate-x-1' 
                        : 'text-slate-600'
                    }`} />
                  </div>
                </CardContent>
              </Card>

              {/* Módulo Aprovações - Apenas para Gestores */}
              {jornyxUser?.is_gestor && (
                <Card 
                  className={`border-0 backdrop-blur cursor-pointer transition-all group ${
                    pendentesAprovacao > 0 
                      ? 'bg-gradient-to-r from-purple-500/30 to-pink-600/20 hover:from-purple-500/40 hover:to-pink-600/30 ring-1 ring-purple-500/50'
                      : 'bg-gradient-to-r from-purple-500/20 to-pink-600/10 hover:from-purple-500/30 hover:to-pink-600/20'
                  }`}
                  onClick={handleAprovacoesClick}
                  data-testid="card-aprovacoes"
                >
                  <CardContent className="py-5">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-xl shadow-lg bg-purple-500 shadow-purple-500/30 relative">
                        <UserCheck className="w-6 h-6 text-white" />
                        {pendentesAprovacao > 0 && (
                          <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                            {pendentesAprovacao > 9 ? '9+' : pendentesAprovacao}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-white">Aprovações</h4>
                          {pendentesAprovacao > 0 ? (
                            <Badge className="bg-amber-500 text-white text-xs animate-pulse">
                              {pendentesAprovacao} pendente{pendentesAprovacao > 1 ? 's' : ''}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-purple-300 border-purple-400/50 text-xs">
                              Gestor
                            </Badge>
                          )}
                        </div>
                        <p className="text-slate-400 text-sm mt-0.5">
                          Aprovar justificativas da equipe
                        </p>
                        {jornyxUser?.subordinados_count > 0 && (
                          <p className="text-xs text-purple-400 mt-1">
                            {jornyxUser.subordinados_count} subordinado(s)
                          </p>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-purple-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Módulo Quadro de Presença */}
              <Card 
                className={`border-0 backdrop-blur cursor-pointer transition-all group ${
                  hasJornyxAccess 
                    ? 'bg-gradient-to-r from-cyan-500/20 to-teal-600/10 hover:from-cyan-500/30 hover:to-teal-600/20' 
                    : 'bg-white/5 opacity-60'
                }`}
                onClick={handleQuadroPresencaClick}
                data-testid="card-quadro-presenca"
              >
                <CardContent className="py-5">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl shadow-lg ${
                      hasJornyxAccess 
                        ? 'bg-cyan-500 shadow-cyan-500/30' 
                        : 'bg-slate-600'
                    }`}>
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-white">Quadro de Presença</h4>
                      </div>
                      <p className="text-slate-400 text-sm mt-0.5">
                        {hasJornyxAccess 
                          ? 'Visão diária da equipe'
                          : 'Sem vínculo - Contate o RH'
                        }
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 transition-transform ${
                      hasJornyxAccess 
                        ? 'text-cyan-400 group-hover:translate-x-1' 
                        : 'text-slate-600'
                    }`} />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Info sobre CPF */}
            {!user.cpf && (
              <Card className="border-0 bg-amber-500/10 backdrop-blur">
                <CardContent className="py-4">
                  <p className="text-amber-300 text-sm">
                    <strong>Atenção:</strong> Seu CPF não está cadastrado. 
                    Atualize seu perfil para vincular ao controle de ponto automaticamente.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </main>

        {/* Footer */}
        <footer className="px-4 py-4 border-t border-white/10">
          <p className="text-center text-slate-500 text-xs">
            Jornyx • Controle de Ponto
          </p>
        </footer>
      </div>

      {/* Modal Bloqueante - Notificação Disciplinar Pendente */}
      <Dialog open={!!notificacaoBloqueante} onOpenChange={() => {}}>
        <DialogContent 
          className="max-w-md bg-slate-900 border-red-500/50 text-white"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Scale className="w-5 h-5" />
              Notificação Disciplinar
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Você possui uma notificação pendente que requer sua atenção imediata.
            </DialogDescription>
          </DialogHeader>
          
          {notificacaoBloqueante && (
            <div className="space-y-4">
              {/* Tipo da notificação */}
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <span className="text-red-300 font-medium block mb-1">
                  {notificacaoBloqueante.tipo === 'advertencia_verbal' && 'Advertência Verbal'}
                  {notificacaoBloqueante.tipo === 'advertencia_escrita' && 'Advertência Escrita'}
                  {notificacaoBloqueante.tipo === 'suspensao' && `Suspensão de ${notificacaoBloqueante.dias_suspensao || 1} dia(s)`}
                  {notificacaoBloqueante.tipo === 'aviso_demissao' && 'Aviso de Demissão'}
                </span>
                <span className="text-slate-400 text-sm">
                  Data do fato: {formatarData(notificacaoBloqueante.data_ocorrencia)}
                </span>
              </div>
              
              {/* Texto formal */}
              <div className="bg-slate-800/50 rounded-lg p-4 max-h-48 overflow-y-auto">
                <p className="text-slate-300 text-sm whitespace-pre-line leading-relaxed">
                  {notificacaoBloqueante.texto_formal || 'Carregando...'}
                </p>
              </div>
              
              {/* Aviso */}
              <div className="flex items-start gap-2 text-amber-400 text-sm">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Você precisa confirmar o recebimento para continuar usando o aplicativo. 
                  Após confirmar, poderá abrir uma contestação se desejar.
                </span>
              </div>
              
              {/* Botão */}
              <Button 
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmarRecebimentoNotificacao}
                disabled={confirmandoRecebimento}
                data-testid="btn-confirmar-recebimento"
              >
                {confirmandoRecebimento ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Confirmar Recebimento
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnifiedHome;
